import { tableColumnDefaults, tableDefaultColumnOrder } from './TableColumnPolicy';
import { computed, createModel, signal } from '@preact/signals-react';
import type { ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { ConcreteDataType } from '@thinkalpha/language-services';
import { SortModel } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { OutputColumnWithMetadata } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { ColumnTemplate, TemplateColumn } from '@thinkalpha/ui-types/contracts/column-template';
import type { TableClass, TableWidgetViewData } from '@thinkalpha/ui-types/contracts/workspace';
import { isEqual } from 'es-toolkit';
import { z } from 'zod';

const columnWidthsSchema = z.record(z.string(), z.number());
const columnVisibilitySchema = z.record(z.string(), z.boolean());

// Older layouts can omit unset fields. Keep the runtime records concrete and
// encode canonical values so a subsequent save no longer needs normalization.
export const tableViewDataSchema = z.object({
    sort: SortModel.nullable().default(null),
    columnWidths: z.codec(columnWidthsSchema.nullish(), columnWidthsSchema, {
        decode: (widths) => widths ?? {},
        encode: (widths) => widths,
    }),
    columnOrder: z.array(z.string()).nullable().default(null),
    columnVisibility: z.codec(columnVisibilitySchema.nullish(), columnVisibilitySchema, {
        decode: (visibility) => visibility ?? {},
        encode: (visibility) => visibility,
    }),
    pinnedColumns: z
        .record(z.string(), z.union([z.boolean(), z.literal('left'), z.literal('right')]))
        .nullable()
        .default(null),
});

export const tableGridStateSchema = z.object({
    viewData: tableViewDataSchema.nullable().default(null),
});

export type TableGridState = z.output<typeof tableGridStateSchema>;

export interface TableGridModelShape {
    readonly columnTemplate: ReadonlySignal<ColumnTemplate>;
    readonly dirty: ReadonlySignal<boolean>;
    readonly resolvedColumns: ReadonlySignal<OutputColumnWithMetadata[]>;
    readonly symbolIdField: ReadonlySignal<string | undefined>;
    readonly viewData: ReadonlySignal<TableWidgetViewData | null>;
    toJSON(): TableGridState;
    updateColumnTemplate(columnTemplate: ColumnTemplate): void;
    markClean(): void;
}

export interface TableGridModelInit {
    columns: ReadonlySignal<OutputColumnWithMetadata[]>;
    persisted: TableGridState;
    tableClass: ReadonlySignal<TableClass | null>;
}

function orderedColumns(
    columns: readonly OutputColumnWithMetadata[],
    columnOrder: readonly string[] | null | undefined,
): OutputColumnWithMetadata[] {
    if (columnOrder === null || columnOrder === undefined) {
        return [...columns];
    }

    const columnsById = new Map<string, OutputColumnWithMetadata>(
        columns.map((column) => [column.id, column] as const),
    );
    const ordered: OutputColumnWithMetadata[] = [];
    for (const columnId of columnOrder) {
        const column = columnsById.get(columnId);
        if (column !== undefined) {
            ordered.push(column);
            columnsById.delete(columnId);
        }
    }
    for (const column of columns) {
        if (columnsById.has(column.id)) {
            ordered.push(column);
        }
    }
    return ordered;
}

function columnPin(value: boolean | 'left' | 'right' | undefined): 'left' | 'right' | undefined {
    return value === true ? 'left' : value === false ? undefined : value;
}

export function tableSymbolIdField(columns: readonly OutputColumnWithMetadata[]): string | undefined {
    return (
        columns.find((column) => column.matchSlug === Slugs.symbol)?.id ??
        columns.find((column) => column.unit === 'symbol')?.id ??
        columns.find((column) => column.id.trim().toLowerCase() === 'symbol')?.id
    );
}

function toTemplateColumn(
    column: OutputColumnWithMetadata,
    viewData: TableWidgetViewData | null,
    sortIndexById: ReadonlyMap<string, number>,
    tableClass: TableClass | null,
): TemplateColumn {
    const sort = viewData?.sort?.[column.id];
    const defaults = tableColumnDefaults(column, tableClass);
    const shared = {
        id: column.id,
        name: defaults.name,
        dataType: column.dataType as ConcreteDataType,
        unit: typeof column.unit === 'string' ? column.unit : null,
        visible: viewData === null ? defaults.visible : (viewData.columnVisibility[column.id] ?? defaults.visible),
        width: viewData?.columnWidths[column.id],
        pinned: columnPin(viewData?.pinnedColumns?.[column.id]),
        sort: sort === 'ascending' ? ('asc' as const) : sort === 'descending' ? ('desc' as const) : undefined,
        sortIndex: sortIndexById.get(column.id),
        displaySettings: defaults.displaySettings,
        options: column.options,
    };

    if (column.type === 'formula') {
        return {
            ...shared,
            type: 'formula',
            imports: column.imports,
            formula: column.formula,
        };
    }

    return {
        ...shared,
        type: 'indicator',
        indicator: column.indicator,
    };
}

/** Projects raw legacy-table output metadata plus controlled widget layout into the ADR 0014 adapter input. */
export function tableColumnTemplate(
    columns: readonly OutputColumnWithMetadata[],
    viewData: TableWidgetViewData | null,
    tableClass: TableClass | null = null,
): ColumnTemplate {
    const sortIndexById = new Map<string, number>(
        Object.keys(viewData?.sort ?? {}).map((columnId, index) => [columnId, index] as const),
    );

    return {
        name: 'Table',
        key: 'table',
        isTemplate: false,
        columns: orderedColumns(
            columns,
            viewData === null ? tableDefaultColumnOrder(tableClass) : viewData.columnOrder,
        ).map((column) => toTemplateColumn(column, viewData, sortIndexById, tableClass)),
        gridDisplaySettings: [],
        rowBasedDisplaySettings: [],
    };
}

/** Reduces the controlled template emitted by TableModelViewer back to Table widget persistence. */
export function tableViewDataFromColumnTemplate(columnTemplate: ColumnTemplate): TableWidgetViewData {
    const sortedColumns = columnTemplate.columns
        .filter((column) => column.sort !== undefined)
        .toSorted((left, right) => (left.sortIndex ?? 0) - (right.sortIndex ?? 0));

    return {
        sort: Object.fromEntries(
            sortedColumns.map((column) => [
                column.id,
                column.sort === 'asc' ? ('ascending' as const) : ('descending' as const),
            ]),
        ),
        columnWidths: Object.fromEntries(
            columnTemplate.columns.flatMap((column) =>
                column.width === undefined ? [] : ([[column.id, column.width]] as const),
            ),
        ),
        columnOrder: columnTemplate.columns.map((column) => column.id),
        columnVisibility: Object.fromEntries(columnTemplate.columns.map((column) => [column.id, column.visible])),
        pinnedColumns: Object.fromEntries(columnTemplate.columns.map((column) => [column.id, column.pinned ?? false])),
    };
}

export const TableGridModel: ModelConstructor<TableGridModelShape, [TableGridModelInit]> = createModel<
    TableGridModelShape,
    [TableGridModelInit]
>((init: TableGridModelInit) => {
    const viewData = signal<TableWidgetViewData | null>(init.persisted.viewData);
    const cleanViewData = signal<TableWidgetViewData | null>(init.persisted.viewData);
    const dirty = computed(() => !isEqual(viewData.value, cleanViewData.value));
    const columnTemplate = computed(() =>
        tableColumnTemplate(init.columns.value, viewData.value, init.tableClass.value),
    );
    const symbolIdField = computed(() => tableSymbolIdField(init.columns.value));

    return {
        columnTemplate,
        dirty,
        resolvedColumns: init.columns,
        symbolIdField,
        viewData,
        toJSON(): TableGridState {
            return { viewData: viewData.value };
        },
        updateColumnTemplate(updatedColumnTemplate: ColumnTemplate): void {
            const updatedViewData = tableViewDataFromColumnTemplate(updatedColumnTemplate);
            if (!isEqual(updatedViewData, viewData.value)) {
                viewData.value = updatedViewData;
            }
        },
        markClean(): void {
            cleanViewData.value = viewData.value;
        },
    };
});

export type TableGridModel = InstanceType<typeof TableGridModel>;
